import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import {
  getIndicatorConfig,
  getIndicator,
  getFullDaysDiff,
  datetimeIsStartOfDay,
} from 'utils/common';

import { Wrapper, DailyScoreInfo, ArrowButton, Dot, Tip } from './elements';
import { useText, useFormatDate } from 'common/hooks';
import { INDICATOR, ICON, DATE_FORMAT } from 'resources/constants';

const deps = {
  isLoadingDailyUserStats: state.challenge.isLoading.dailyUserStats,
  challenge: state.challenge.campaign.challenge,
  indicator: state.challenge.campaign.indicator,
  dailyUserStats: state.challenge.stats.daily.user,
  userCampaign: state.challenge.userCampaign,
  selectedDayIndex: state.challenge.log.selectedDayIndex,
  selectedDatetime:
    state.challenge.campaign.challenge.startOfDays[
      state.challenge.log.selectedDayIndex
    ],
  showTutorial: state.challenge.tutorial.show,
  tutorialActivityLogged: state.challenge.tutorial.tutorialActivityLogged,
  showBacklogTip: state.challenge.info.showBacklogTip,
  previousDayArrowClicked: sequences`challenge.log.previousDayArrowClicked`,
  nextDayArrowClicked: sequences`challenge.log.nextDayArrowClicked`,
  indicatorModalClicked: sequences`challenge.info.indicatorModalClicked`,
};

function DaySelector({
  isLoadingDailyUserStats,
  challenge,
  indicator = INDICATOR.co2,
  dailyUserStats = {},
  userCampaign,
  selectedDayIndex,
  selectedDatetime,
  showTutorial,
  tutorialActivityLogged,
  showBacklogTip,
  previousDayArrowClicked,
  nextDayArrowClicked,
  indicatorModalClicked,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const formatDate = useFormatDate();

  if (isLoadingDailyUserStats) {
    return (
      <Wrapper>
        <DailyScoreInfo
          value={0}
          inactive
          description={t(`challenge.ChaDSc1.your_${indicator}_savings_today`)}
          tooltip={t(`challenge.ChaDSc1.your_${indicator}_savings_today`)}
          title={t('challenge.ChaDSc1.day', { day: '' })}
          locale={t.fullLocale}
        />
      </Wrapper>
    );
  }

  const currentDayIndex = getFullDaysDiff(challenge.startDatetime);
  const lastDayIndex = getFullDaysDiff(
    challenge.startDatetime,
    challenge.endDatetime
  );

  let todaysScore = dailyUserStats[selectedDatetime] || { co2: 0, points: 0 };

  if (tutorialActivityLogged) {
    todaysScore = {
      ...(dailyUserStats[selectedDatetime] || {}),
      co2: (todaysScore.co2 || 0) + 1606670,
      points: (todaysScore.points || 0) + 5000000,
    };
  }

  const { icon, color } = getIndicatorConfig(indicator);

  return (
    <Wrapper>
      <Tip
        showTip={showBacklogTip}
        text={t('challenge.tips.log_missed_activities')}
        isBelow
      >
        <ArrowButton
          disabled={selectedDayIndex === 0}
          onClick={() => previousDayArrowClicked()}
          icon={ICON.keyboardArrowLeft}
        />
      </Tip>
      <DailyScoreInfo
        description={
          selectedDayIndex === currentDayIndex &&
          Date.now() < challenge.endDatetime
            ? t(`challenge.ChaDSc1.your_${indicator}_savings_today`)
            : t(`challenge.ChaDSc1.your_${indicator}_savings`)
        }
        tooltip={
          selectedDayIndex === currentDayIndex &&
          Date.now() < challenge.endDatetime
            ? t(`challenge.ChaDSc1.your_${indicator}_savings_today_tooltip`)
            : t(`challenge.ChaDSc1.your_${indicator}_savings_tooltip`)
        }
        iconColor={color}
        icon={icon}
        value={getIndicator(todaysScore, indicator)}
        decimals={indicator === 'co2' ? 2 : 0}
        locale={t.fullLocale}
        inactive={!userCampaign}
        onClick={
          showTutorial ? null : () => indicatorModalClicked({ indicator })
        }
        title={formatDate(
          selectedDatetime,
          datetimeIsStartOfDay(selectedDatetime)
            ? DATE_FORMAT.DAY_MONTH_SHORT
            : DATE_FORMAT.DAY_MONTH_TIME_SHORT
        )}
      />
      <Dot show={showTutorial === 2} />
      <ArrowButton
        disabled={selectedDayIndex >= Math.min(lastDayIndex, currentDayIndex)}
        onClick={() => nextDayArrowClicked()}
        icon={ICON.keyboardArrowRight}
      />
    </Wrapper>
  );
}
export default connect(deps, DaySelector);
